import { Process } from '@apps/file-process/file-process.state';
import { firstValueFrom, Observable } from 'rxjs';
import { filter, first, map } from 'rxjs/operators';
import { select, Store } from '@ngrx/store';
import { RootState } from '@root/app.state';
import { WritableDraft } from 'immer';
import { ErrorDto } from './error.model';
import { deepCopyJson } from './utilities/deepclone';

export const ResourceStatuses = {
  NONE: 'NONE',
  SUCCEEDED: 'SUCCEEDED',
  FAILED: 'FAILED',
  PENDING: 'PENDING',
} as const;

export type ResourceStatus = (typeof ResourceStatuses)[keyof typeof ResourceStatuses];

export interface Error {
  [index: string]: string[];
}

export interface RequestError {
  errors: Error;
  status: number;
}

export interface ResourceState<T> {
  data: T;
  error?: RequestError;
  status: ResourceStatus;
}

export interface ResourceStateNone<T> {
  status: 'NONE';
  data: T | null;
  error: RequestError | null;
}

export interface ResourceStatePending<T> {
  status: 'PENDING';
  data: T | null;
  error: RequestError | null;
}

export interface ResourceStateFailed<T> {
  status: 'FAILED';
  data: T | null;
  error: RequestError;
}

export interface ResourceStateSucceeded<T> {
  status: 'SUCCEEDED';
  data: T;
  error: null;
}

export type SafeResourceState<T> = ResourceStateNone<T> | ResourceStatePending<T> | ResourceStateFailed<T> | ResourceStateSucceeded<T>;

export function createRequestError(status: number, errors: ErrorDto): RequestError {
  return {
    status,
    errors: { ...errors },
  };
}

export function resetFileNew(process: WritableDraft<Process>): void {
  process?.sections?.forEach((s) => {
    s.positions?.forEach((p) => {
      p.files
        ?.filter((f) => !!f)
        .forEach((f) => {
          f.isNew = false;
        });
    });
  });
}

export const filterSucceededResource =
  () =>
  <T>(source: Observable<SafeResourceState<T> | ResourceState<T>>): Observable<T> =>
    source.pipe(
      filter(resourceStateIsSucceeded),
      map((resourceState) => resourceState.data),
    );

const resourceStateIsSucceeded = <T>(resourceState: SafeResourceState<T> | ResourceState<T>): resourceState is ResourceStateSucceeded<T> =>
  resourceState.status === 'SUCCEEDED';

export const resourceLoadSucceeded = async (
  store$: Store<RootState>,
  ...resourceStateSelectors: ((source: object) => ResourceState<unknown> | SafeResourceState<unknown>)[]
): Promise<boolean> => {
  const successResults = await Promise.all(
    resourceStateSelectors.map((selector) =>
      firstValueFrom(
        store$.pipe(
          select(selector),
          first((x) => x.status === ResourceStatuses.FAILED || x.status === ResourceStatuses.SUCCEEDED),
          map((x) => x.status === ResourceStatuses.SUCCEEDED),
        ),
      ),
    ),
  );

  return successResults.every((success) => success);
};
